import ExcelJS from "exceljs";
import { RAMA_LABELS, getCategoriaDetalle } from "./registryImportColumns";

// Fila 1: clave técnica de cada columna (la lee el importador). Es visible y
// editable a propósito: si pegas tus propias columnas de datos, escribe aquí
// la clave correspondiente (ver hoja "Claves disponibles"); las columnas sin
// clave reconocida se ignoran.
// Fila 2: etiqueta humana, solo de referencia (no se usa para importar).
// Datos desde la fila 3.
const HEADER_KEY_ROW = 1;
const HEADER_LABEL_ROW = 2;
const FIRST_DATA_ROW = 3;

function buildInstruccionesSheet(workbook, eventDetail) {
  const sheet = workbook.addWorksheet("Instrucciones");
  sheet.getColumn(1).width = 110;

  const nombreEvento = eventDetail && (eventDetail.nombreEvento || eventDetail.iDEvento);

  const lineas = [
    { texto: `Plantilla de importación de inscripciones${nombreEvento ? " - " + nombreEvento : ""}`, bold: true, size: 14 },
    { texto: "" },
    { texto: "Instrucciones:", bold: true },
    { texto: "- Hoja \"Datos\": todas las modalidades van en la misma hoja, una fila por atleta." },
    { texto: "- Columna \"Modalidad y Categoría\": copia el texto exacto de la columna \"Etiqueta\" de la hoja \"Modalidad y Categoría\" para indicar a qué modalidad, género y categoría pertenece cada atleta." },
    { texto: "- Fila 1 de la hoja \"Datos\": es la clave técnica de cada columna, no la borres. Si pegas columnas propias con tus datos, escribe en esa fila la clave que corresponda (ver hoja \"Claves disponibles\"); las columnas sin clave reconocida se ignoran, así que puedes pegar tu archivo original tal cual y solo ir etiquetando las columnas que quieras usar." },
    { texto: "- No hay listas desplegables: escribe los valores como texto libre. Si un valor no coincide con ninguna opción conocida (por ejemplo el nombre de una talla), el importador te avisa pero te deja importarlo igual, guardando el valor tal como lo escribiste." },
    { texto: "- Los campos marcados con * en la fila 2 son siempre obligatorios. Los demás campos (los que dependen de la modalidad/categoría, como tallas o datos de relevos) se validan según la modalidad y categoría que hayas puesto en cada fila." },
    { texto: "- Fecha de nacimiento en formato AAAA-MM-DD." },
    { texto: "- La columna \"Número\" es opcional: si la dejas en blanco, el sistema asignará el folio automáticamente en un paso posterior (igual que en un registro manual). Si la llenas, se usará ese número." },
    { texto: "- Columnas de productos: usa la columna \"... - Cantidad\" para indicar cuántos compró el atleta (déjala en blanco o en 0 si no aplica). Las columnas de datos del producto (por ejemplo talla) solo son necesarias si la cantidad es mayor a 0." },
  ];

  lineas.forEach((linea, i) => {
    const cell = sheet.getCell(i + 1, 1);
    cell.value = linea.texto;
    cell.alignment = { wrapText: true };
    if (linea.bold) cell.font = { bold: true, size: linea.size || 12 };
  });

  return sheet;
}

// Hoja de referencia: una fila por cada combinación real de
// modalidad+género+categoría del evento. La columna "Etiqueta" trae un texto
// sugerido (editable) que el organizador puede ajustar para que coincida con
// como esa combinación aparece en su archivo de origen.
function buildModalidadCategoriaSheet(workbook, eventSubEventsRegistry) {
  const sheet = workbook.addWorksheet("Modalidad y Categoría");
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const headers = ["Etiqueta (cópiala en la columna \"Modalidad y Categoría\" de la hoja Datos)", "Modalidad", "Código modalidad", "Código categoría", "Género"];
  headers.forEach((titulo, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = titulo;
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2EFDA" } };
  });
  sheet.getColumn(1).width = 50;
  sheet.getColumn(2).width = 28;
  [3, 4, 5].forEach((c) => (sheet.getColumn(c).width = 18));

  let fila = 2;
  Object.values(eventSubEventsRegistry || {})
    .filter((modalidad) => modalidad && !modalidad.hide)
    .forEach((modalidad) => {
      getCategoriaDetalle(modalidad).forEach((c) => {
        const etiqueta = `${modalidad.descripcionModalidad} - ${RAMA_LABELS[c.rama] || c.rama} - ${c.nombreCategoria}`;
        sheet.getCell(fila, 1).value = etiqueta;
        sheet.getCell(fila, 2).value = modalidad.descripcionModalidad;
        sheet.getCell(fila, 3).value = modalidad.codigoModalidad;
        sheet.getCell(fila, 4).value = c.categoria;
        sheet.getCell(fila, 5).value = c.rama;
        fila++;
      });
    });

  return sheet;
}

// Hoja de referencia con todas las claves técnicas disponibles, para cuando
// el organizador agrega columnas propias y necesita saber qué escribir en la
// fila 1 de la hoja "Datos".
function buildClavesSheet(workbook, columnasFijas, columnasDinamicas) {
  const sheet = workbook.addWorksheet("Claves disponibles");
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  ["Clave (fila 1 de la hoja Datos)", "Campo", "Tipo", "Opciones válidas"].forEach((titulo, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = titulo;
    cell.font = { bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2EFDA" } };
  });
  sheet.getColumn(1).width = 30;
  sheet.getColumn(2).width = 40;
  sheet.getColumn(3).width = 16;
  sheet.getColumn(4).width = 60;

  let fila = 2;
  [...columnasFijas, ...columnasDinamicas].forEach((col) => {
    sheet.getCell(fila, 1).value = col.key;
    sheet.getCell(fila, 2).value = col.label;
    sheet.getCell(fila, 3).value = col.type;
    if ((col.type === "select" || col.type === "checkbox") && col.options && col.options.length > 0) {
      const cell = sheet.getCell(fila, 4);
      cell.value = col.options.join(", ");
      cell.alignment = { wrapText: true };
    }
    fila++;
  });

  return sheet;
}

function buildDatosSheet(workbook, columnasFijas, columnasDinamicas) {
  const sheet = workbook.addWorksheet("Datos");
  sheet.views = [{ state: "frozen", ySplit: HEADER_LABEL_ROW }];

  const columns = [...columnasFijas, ...columnasDinamicas];

  columns.forEach((col, index) => {
    const colNumber = index + 1;

    const keyCell = sheet.getCell(HEADER_KEY_ROW, colNumber);
    keyCell.value = col.key;
    keyCell.font = { bold: true, color: { argb: "FF808080" } };
    keyCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };

    const labelCell = sheet.getCell(HEADER_LABEL_ROW, colNumber);
    labelCell.value = col.label + (col.required ? " *" : "");
    labelCell.font = { bold: true };
    labelCell.alignment = { wrapText: true, vertical: "middle" };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: col.required ? "FFFCE4D6" : "FFE2EFDA" },
    };
    if (col.help) labelCell.note = col.help;

    sheet.getColumn(colNumber).width = Math.max(16, Math.min(42, col.label.length + 4));
  });

  return sheet;
}

export async function buildRegistryTemplateWorkbook(eventDetail, eventSubEventsRegistry, registryConfiguration, columnasFijas, columnasDinamicas) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Dashport";
  workbook.created = new Date();

  buildInstruccionesSheet(workbook, eventDetail);
  buildModalidadCategoriaSheet(workbook, eventSubEventsRegistry);
  buildClavesSheet(workbook, columnasFijas, columnasDinamicas);
  buildDatosSheet(workbook, columnasFijas, columnasDinamicas);

  return workbook;
}
